import { useState } from 'react';

import { Badge, Button, Card, Divider, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { dataSimples, diaIso, moeda } from '@/comuns/formato';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import { TIPOS_DE_CONTA, efeitoDasTransferencias } from '@/data/financeiroAvulso';
import type { CadastrosFinanceiros, ContaNoCadastro, Transferencia } from '@/data/financeiroAvulso';
import type { ContaFinanceira, Movimentacao } from '@/data/tipos';
import { ConciliacaoModal } from './ConciliacaoModal';
import { ContaModal } from './ContaModal';
import { TransferenciaModal } from './TransferenciaModal';

export interface ContasFinanceirasProps {
    /** As contas ativas, com o saldo do que já liquidou. */
    contas: ContaFinanceira[];
    cadastros: CadastrosFinanceiros;
    transferencias: Transferencia[];
    movimentacoes: Movimentacao[];
    /** A preferência "Conciliação bancária". Desligada, a importação de OFX não abre. */
    conciliacaoLigada: boolean;
    onMudou: () => void;
}

/**
 * Contas financeiras — saldo de hoje, transferência, conciliação e cadastro.
 *
 * O saldo é o que já liquidou mais as transferências. As transferências entram
 * aqui, e não no saldo que o módulo calcula, porque são dinheiro que muda de
 * conta: o consolidado não muda, e o fluxo de caixa não as conta.
 */
export function ContasFinanceiras({ contas, cadastros, transferencias, movimentacoes, conciliacaoLigada, onMudou }: ContasFinanceirasProps) {
    const [transferindo, setTransferindo] = useState<string | 'nenhuma' | null>(null);
    const [conciliando, setConciliando] = useState(false);
    const [editando, setEditando] = useState<ContaNoCadastro | 'nova' | null>(null);
    const [detalhe, setDetalhe] = useState<string | null>(null);

    const efeito = efeitoDasTransferencias(transferencias, diaIso());
    const saldoDe = (c: ContaFinanceira) => c.saldoCentavos + (efeito.get(c.id) ?? 0);
    const consolidado = contas.reduce((s, c) => s + saldoDe(c), 0);
    const inativas = cadastros.contas.filter((c) => !c.ativa);
    const rotuloDoTipo = (t: ContaFinanceira['tipo']) => TIPOS_DE_CONTA.find((x) => x.valor === t)?.rotulo ?? t;
    const contaDoDetalhe = cadastros.contas.find((c) => c.id === detalhe);
    const saldoDoDetalhe = contas.find((c) => c.id === detalhe);

    return (
        <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
                {!conciliacaoLigada && (
                    <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                        Conciliação desligada em Configurações › Preferências do sistema
                    </span>
                )}
                <Button variant="secondary" iconLeft={<Icon name="file-check-2" size={15} />} disabled={!conciliacaoLigada || contas.length === 0} onClick={() => setConciliando(true)}>
                    Conciliar
                </Button>
                <Button variant="secondary" iconLeft={<Icon name="arrow-left-right" size={15} />} disabled={contas.length < 2} onClick={() => setTransferindo('nenhuma')}>
                    Transferir
                </Button>
                <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => setEditando('nova')}>Nova conta</Button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(min(260px,100%),1fr))', gap: 12 }}>
                {contas.map((c) => {
                    const transferido = efeito.get(c.id) ?? 0;
                    return (
                        <Card
                            key={c.id} title={c.nome} subtitle={c.banco ?? rotuloDoTipo(c.tipo)} padding={18}
                            actions={<Button size="sm" variant="ghost" onClick={() => setDetalhe(c.id)} aria-label={`Abrir conta ${c.nome}`}>Detalhes</Button>}
                        >
                            <p style={{ margin: 0, fontFamily: 'var(--font-ui)', fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: saldoDe(c) < 0 ? 'var(--danger-strong)' : 'var(--text-primary)' }}>
                                {moeda(saldoDe(c))}
                            </p>
                            <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--text-tertiary)' }}>
                                saldo com o que já liquidou{transferido !== 0 ? ' e as transferências' : ''}
                            </p>
                        </Card>
                    );
                })}
            </div>

            <Alert tone="neutral" title="Saldo consolidado" dismissible={false}>
                {moeda(consolidado)} distribuídos em {contas.length} {contas.length === 1 ? 'conta' : 'contas'}.
                {transferencias.length > 0 && ' Transferência não muda o consolidado: o dinheiro só trocou de conta.'}
            </Alert>

            {inativas.length > 0 && (
                <Card title="Contas desativadas" subtitle="Fora das listas de escolha; o histórico continua" padding={0}>
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {inativas.map((c, i) => (
                            <li key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', borderTop: i === 0 ? 'none' : '1px solid var(--border-subtle)' }}>
                                <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600 }}>{c.nome}</span>
                                <Badge tone="neutral" dot>Inativa</Badge>
                                <Button size="sm" variant="secondary" onClick={() => setEditando(c)}>Editar</Button>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}

            {transferindo && (
                <TransferenciaModal
                    contas={contas.map((c) => ({ id: c.id, nome: c.nome, saldoCentavos: saldoDe(c) }))}
                    {...(transferindo !== 'nenhuma' ? { origemInicial: transferindo } : {})}
                    onFechar={() => setTransferindo(null)} onTransferido={onMudou}
                />
            )}
            {editando && <ContaModal alvo={editando} onFechar={() => setEditando(null)} onSalva={onMudou} />}
            {conciliando && (
                <ConciliacaoModal contas={cadastros.contas.filter((c) => c.ativa)} onFechar={() => setConciliando(false)} onMudou={onMudou} />
            )}
            {contaDoDetalhe && saldoDoDetalhe && (
                <DetalheDaContaModal
                    conta={contaDoDetalhe}
                    saldoCentavos={saldoDe(saldoDoDetalhe)}
                    movimentacoes={movimentacoes.filter((m) => m.conta === contaDoDetalhe.nome)}
                    transferencias={transferencias.filter((t) => t.origemId === contaDoDetalhe.id || t.destinoId === contaDoDetalhe.id)}
                    onFechar={() => setDetalhe(null)}
                    onEditar={() => { setDetalhe(null); setEditando(contaDoDetalhe); }}
                    onTransferir={() => { setDetalhe(null); setTransferindo(contaDoDetalhe.id); }}
                    onMudou={onMudou}
                />
            )}
        </>
    );
}

interface DetalheDaContaModalProps {
    conta: ContaNoCadastro;
    saldoCentavos: number;
    movimentacoes: Movimentacao[];
    transferencias: Transferencia[];
    onFechar: () => void;
    onEditar: () => void;
    onTransferir: () => void;
    onMudou: () => void;
}

/** Detalhe da conta: saldo, dados e o que passou por ela por último. */
function DetalheDaContaModal({ conta, saldoCentavos, movimentacoes, transferencias, onFechar, onEditar, onTransferir, onMudou }: DetalheDaContaModalProps) {
    const aviso = useAviso();
    const [ocupado, setOcupado] = useState(false);

    const linhas = [
        ...movimentacoes.map((m) => ({
            id: m.id, data: m.pagoEm, descricao: m.descricao,
            valor: m.tipo === 'receber' ? m.liquidoCentavos : -m.valorCentavos,
        })),
        ...transferencias.map((t) => ({
            id: t.id, data: t.data,
            descricao: t.origemId === conta.id ? `Transferência para ${t.destino}` : `Transferência de ${t.origem}`,
            valor: t.origemId === conta.id ? -t.valorCentavos : t.valorCentavos,
        })),
    ].sort((a, b) => b.data.localeCompare(a.data)).slice(0, 6);

    const desativar = async () => {
        setOcupado(true);
        try {
            await dados().salvarConta(conta.id, {
                nome: conta.nome, tipo: conta.tipo, saldoInicialCentavos: conta.saldoInicialCentavos, ativa: false,
                ...(conta.banco ? { banco: conta.banco } : {}),
                ...(conta.agencia ? { agencia: conta.agencia } : {}),
                ...(conta.numero ? { numero: conta.numero } : {}),
            });
            aviso({ title: 'Conta desativada', description: conta.nome, tone: 'warning' });
            onMudou();
            onFechar();
        } catch (e) {
            aviso({ title: 'Não foi possível desativar a conta', description: e instanceof Error ? e.message : 'Tente de novo.', tone: 'danger' });
        } finally {
            setOcupado(false);
        }
    };

    return (
        <Modal
            open width={560} onClose={onFechar} title={conta.nome}
            description={[conta.banco, conta.agencia && `ag ${conta.agencia}`, conta.numero && `cc ${conta.numero}`].filter(Boolean).join(' · ') || undefined}
            footer={(
                <>
                    <Button variant="ghost" onClick={() => { void desativar(); }} disabled={ocupado}>Desativar</Button>
                    <span style={{ marginLeft: 'auto' }} />
                    <Button variant="secondary" iconLeft={<Icon name="pencil" size={15} />} onClick={onEditar}>Editar</Button>
                    <Button iconLeft={<Icon name="arrow-left-right" size={15} />} onClick={onTransferir}>Transferir</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '14px 16px', background: 'var(--surface-secondary)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-md)' }}>
                    <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13, color: 'var(--text-secondary)' }}>Saldo de hoje</span>
                    <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-ui)', fontSize: 22, fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: saldoCentavos < 0 ? 'var(--danger-strong)' : 'var(--text-primary)' }}>{moeda(saldoCentavos)}</span>
                </div>
                <Divider label="Últimas movimentações" spacing={2} />
                {linhas.length === 0 ? (
                    <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>Nenhuma movimentação no período.</p>
                ) : (
                    <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                        {linhas.map((l) => (
                            <li key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: '1px solid var(--border-subtle)', fontSize: 13 }}>
                                <span style={{ width: 84, color: 'var(--text-tertiary)', fontVariantNumeric: 'tabular-nums' }}>{dataSimples(l.data)}</span>
                                <span style={{ flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.descricao}</span>
                                <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: l.valor < 0 ? 'var(--danger-strong)' : 'var(--success-strong)' }}>
                                    {l.valor < 0 ? '−' : '+'}{moeda(Math.abs(l.valor))}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Modal>
    );
}
